import {
  AlertTriangle,
  Award,
  Calendar,
  ClipboardList,
  CreditCard,
  FileSpreadsheet,
  HandCoins,
  Package,
  Receipt,
  Truck,
  Wallet,
  Wrench,
} from 'lucide-react'
import type { ComponentType } from 'react'

/**
 * 业务链路的「任务节点」元数据 —— 单一来源。
 * 统一任务工作台(卡片网格)、通知铃铛「待办任务」栏、通知中心「待办任务」Tab 都读它,
 * 避免各处各写一份节点清单导致漂移。计数 key 对应后端 get_task_badges 的字段。
 */
export interface TaskNodeMeta {
  key: string
  /** 节点名(待办) */
  label: string
  /** get_task_badges 里的计数字段 */
  badgeKey: string
  /** 该节点的处理页 */
  href: string
  /** 有权处理此节点的角色 */
  roles: string[]
  approvalBizType?: string
  icon: ComponentType<{ size?: number }>
}

export const TASK_NODES: TaskNodeMeta[] = [
  { key: 'invoiceApproval', label: '开票核价', badgeKey: 'invoiceMarketReview', href: '/dashboard/tasks/invoice-approval?tab=review', roles: ['ADMIN', 'MARKET'], icon: Receipt, approvalBizType: 'INVOICE' },
  { key: 'refundApproval', label: '退款审批', badgeKey: 'refundMarketReview', href: '/dashboard/tasks/refund-approval', roles: ['ADMIN', 'MARKET'], icon: Wallet, approvalBizType: 'REFUND' },
  { key: 'reissueApproval', label: '换票审批', badgeKey: 'reissueMarketReview', href: '/dashboard/tasks/reissue-approval', roles: ['ADMIN', 'MARKET'], icon: Receipt, approvalBizType: 'INVOICE_REISSUE' },
  { key: 'refund', label: '待审核退款', badgeKey: 'financePendingRefund', href: '/dashboard/tasks/refund', roles: ['ADMIN', 'FINANCE'], icon: Wallet },
  { key: 'customer', label: '我的待办', badgeKey: 'customerPending', href: '/dashboard/tasks/customer', roles: ['CUSTOMER'], icon: ClipboardList },
  { key: 'directReceipt', label: '不开票待回款', badgeKey: 'financePendingDirectReceipt', href: '/dashboard/tasks/payment-reconcile?tab=direct', roles: ['ADMIN', 'FINANCE'], icon: Wallet },
  { key: 'unmatchedReceipt', label: '到账待核对', badgeKey: 'financeUnmatchedReceipt', href: '/dashboard/tasks/payment-reconcile?tab=unmatched', roles: ['ADMIN', 'FINANCE'], icon: Wallet },
  { key: 'invoiceApplication', label: '待申请开票', badgeKey: 'marketPendingInvoiceApplication', href: '/dashboard/tasks/invoice-approval', roles: ['ADMIN', 'MARKET'], icon: Receipt },
  { key: 'assign', label: '待分配', badgeKey: 'marketPendingAssign', href: '/dashboard/tasks/assign', roles: ['ADMIN', 'MARKET'], icon: Package },
  { key: 'onsite', label: '上门待派单', badgeKey: 'onsitePendingDispatch', href: '/dashboard/tasks/dispatch', roles: ['ADMIN', 'SERVICE'], icon: ClipboardList },
  { key: 'mailReceive', label: '邮寄 / 代送待收件', badgeKey: 'mailReceivePending', href: '/dashboard/tasks/mail-receive', roles: ['ADMIN', 'SERVICE'], icon: Package },
  { key: 'mailDispatch', label: '邮寄待派单', badgeKey: 'mailDispatchPending', href: '/dashboard/tasks/mail-dispatch', roles: ['ADMIN', 'SERVICE'], icon: ClipboardList },
  { key: 'mailReturn', label: '邮寄 / 代送待寄回', badgeKey: 'mailReturnPending', href: '/dashboard/tasks/mail-return', roles: ['ADMIN', 'SERVICE'], icon: Truck },
  { key: 'outsourceTesting', label: '代送检测跟进', badgeKey: 'outsourceTestingPending', href: '/dashboard/tasks/outsource-testing', roles: ['ADMIN', 'SERVICE'], icon: Wrench },
  { key: 'mailDailyExport', label: '邮寄订单汇总导出', badgeKey: 'mailDailyExportPending', href: '/dashboard/service/mail-workbench?dailyExport=1', roles: ['ADMIN', 'SERVICE'], icon: FileSpreadsheet },
  { key: 'engineerOnsite', label: '上门检测', badgeKey: 'engineerScheduledOnsite', href: '/dashboard/tasks/inspect-onsite', roles: ['ADMIN', 'ENGINEER'], icon: Wrench },
  { key: 'engineerMail', label: '邮寄检测', badgeKey: 'engineerScheduledMail', href: '/dashboard/tasks/inspect-mail', roles: ['ADMIN', 'LAB_ENGINEER'], icon: Wrench },
  { key: 'voucher', label: '待审核凭证', badgeKey: 'financePendingVoucher', href: '/dashboard/tasks/voucher-audit', roles: ['ADMIN', 'FINANCE'], icon: CreditCard },
  { key: 'cert', label: '待上传证书', badgeKey: 'certPending', href: '/dashboard/tasks/cert', roles: ['ADMIN', 'SERVICE'], icon: Award },
  { key: 'invoice', label: '待开票', badgeKey: 'financePendingInvoice', href: '/dashboard/tasks/issue-invoice', roles: ['ADMIN', 'FINANCE'], icon: Receipt },
  { key: 'paymentClaim', approvalBizType: 'INVOICE_PAYMENT', label: '待认领回款', badgeKey: 'paymentClaimReview', href: '/dashboard/tasks/payment-claim', roles: ['ADMIN', 'MARKET'], icon: HandCoins },
  { key: 'reconcile', label: '待核销回款', badgeKey: 'financePendingReconcile', href: '/dashboard/tasks/payment-reconcile', roles: ['ADMIN', 'FINANCE'], icon: Wallet },
  { key: 'monthly', label: '月结待审核', badgeKey: 'monthlyPendingAudit', href: '/dashboard/tasks/monthly', roles: ['ADMIN', 'MONTHLY_ADMIN'], icon: Calendar },
  { key: 'exception', label: '待处理异常', badgeKey: 'exceptionPending', href: '/dashboard/tasks/exception', roles: ['ADMIN', 'MARKET'], icon: AlertTriangle },
]
